/**
 * Shared types used across all university data files.
 * Each university imports these types but keeps its own data completely separate.
 */

export type AdmissionType = "equivalent" | "high_school";
export type Track = "scientific" | "literary";
export type Gender = "male" | "female";

export interface Department {
    code: string;
    collegeKey: string;
    departmentKey: string | null;
    equivalentRate: number;
    type: AdmissionType;
    track?: Track;
    gender?: Gender;
    minHighSchool?: number;
    /** Extra score input required to apply for this specialty (e.g. KU French Language Dept). */
    extraScoreField?: "french";
    /** Minimum score on the extra field; gate only — not part of equivalent-rate math. */
    minExtraScore?: number;
    /** When this specialty uses a different equivalent-rate formula than its parent college
     *  (e.g. Edu-Sci humanities tracks use the literary formula). */
    weightsOverride?: CollegeWeights;
    /** Education-stage suffix shown after the specialty name on cards (Primary / Middle & Secondary). */
    stage?: "primary" | "middle_secondary";
    /** Foreign Missions: minimum equivalent rate accepted for this (specialty, country)
     *  in the previous academic year (per MoHE's "Min Accepted Equivalent Rate" table). */
    minAcceptedLastYear?: number;
    /** AASU: minimum high-school percentage that was actually accepted last year,
     *  indexed by track. Source: last page of the 2026/2027 AASU PDF
     *  ("أقل نسبة ثانوية" column). Missing keys → no row published for that
     *  (specialty, track); the UI should fall back to the apply-floor. */
    minAcceptedLastYearByTrack?: Partial<Record<Track, number>>;
    /** PAAET: PDF marks this specialty as "ناجح" (just pass HS) — `minHighSchool`
     *  is set to the Kuwait HS pass floor (60) and the UI shows a "Just pass HS" label. */
    passOnly?: boolean;
    /** i18n key(s) (suffix after `dream_university_`) for extra admission conditions
     *  shown as notes on this specialty's card (e.g. KU Communication Disorders
     *  interview, or the PAAET footnote set). Owned by each section's data module;
     *  rendered generically. A single string shows one line; an array shows each
     *  condition on its own line. */
    additionalConditions?: string | string[];
}

export interface CollegeWeights {
    highSchoolWeight: number;
    arabicWeight: number;
    chemistryWeight: number;
    mathWeight: number;
    englishWeight: number;
    minHighSchool: number | null;
    /** When a college accepts BOTH tracks with different competitive thresholds
     *  (e.g. AASU Business Admin: 80% sci vs 85% lit), this is the literary
     *  threshold; literary students use this instead of minHighSchool. */
    minHighSchoolLiterary?: number | null;
    minEnglish: number | null;
    additionalConditions?: string;
    admissionType: AdmissionType;
}

export interface College {
    key: string;
    emoji: string;
    track?: Track;
    weights: CollegeWeights;
    departments: Department[];
    /** Visual nesting only — child appears beneath the parent on the colleges grid. */
    parentKey?: string;
    /** Optional i18n key for the display name. Defaults to
     *  `dream_university_college_${key}`. Lets one section override the label
     *  for a shared college key (e.g. FM medicine → "الطب البشري") without
     *  affecting any other section. */
    nameKey?: string;
}

/** Resolve the i18n key for a college's display name. Sections can override
 *  via `College.nameKey` so the change stays inside that section's data module. */
export function getCollegeNameKey(college: Pick<College, "key" | "nameKey">): string {
    return college.nameKey ?? `dream_university_college_${college.key}`;
}

export interface UniversityDefinition {
    id: string;
    nameKey: string;
    emoji: string;
    colleges: College[];
    departments: Department[];
    getCollegeMinRate: (collegeKey: string) => number;
    getRequiredFields: (weights: CollegeWeights) => string[];
    getCollegeSpecialties: (collegeKey: string) => Department[];
    hasSelectableSpecialties: (collegeKey: string) => boolean;
}

/** Student-supplied scores fed into a university's equivalent-rate formula.
 *  Each university owns its own calculation function in its data module —
 *  this interface is the shared input contract. */
export interface Scores {
    high_school: number;
    english?: number;
    math?: number;
    arabic?: number;
    chemistry?: number;
    french?: number;
}

/** A single translated line inside a step alert. */
export interface StepAlertLine {
    key: string;
    params?: Record<string, string | number>;
}

export type StepAlertSeverity = "info" | "warning" | "error" | "tip";

/** Optional icon override for an alert. The shared renderer maps these to
 *  Lucide glyphs; when omitted it falls back to a severity-based default. */
export type StepAlertIcon = "info" | "calculator" | "list-checks" | "scale" | "alert-triangle";

/** A section-owned alert shown in a step. Each section's data module decides
 *  its wording, percentages, and whether to show it at all; the shared step
 *  components only render the descriptor. Keeps per-section alert logic fully
 *  isolated inside each module. */
export interface StepAlert {
    severity: StepAlertSeverity;
    titleKey?: string;
    lines: StepAlertLine[];
    /** Optional glyph override (e.g. a calculator for a rate-formula note). */
    iconKey?: StepAlertIcon;
}
